import Col from "antd/es/col";
import Row from "antd/es/row";
import AntDatePicker, {
  RangePickerProps as AntRangePickerProps,
} from "antd/es/date-picker";
import React, {
  CSSProperties,
  FocusEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { TABLET } from "common/constants";
import { v4 as uuid } from "uuid";
import { Dayjs } from "dayjs";
import Link from "common/Typography/Link";
import { typography } from "@shared/common/Themes/typography";
import { getRangePresets } from "@shared/common/RangePicker/utils";
import { useBottomNavigator } from "utils/hooks/misc";
import Drawer from "common/Drawers/Drawer";
import { hexToRGB } from "@shared/utils/misc.ts";
import { getNewLookPlaceholderColor } from "common/Inputs/styles";
import { openMessage } from "utils/misc.tsx";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { formatDate } from "@shared/utils/date";

type RangePickerProps = AntRangePickerProps & {
  localRender?: boolean;
  wrapperStyle?: CSSProperties;
  onPresetsClick?: (value: [Dayjs | null, Dayjs | null]) => void;
  hidePresets?: boolean;
  minValue?: Dayjs;
  maxValue?: Dayjs;
  bottomExtraElement?: React.JSX.Element;
};

const RangePicker = ({
  localRender = true,
  wrapperStyle,
  onPresetsClick,
  hidePresets,
  minValue,
  maxValue,
  bottomExtraElement,
  ...props
}: RangePickerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const containerUUID = useMemo(() => uuid(), []);
  const isMobile = useMedia([`(max-width: ${TABLET})`], [true], false);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [isOpen, setIsOpen] = useState(false);
  const isDrawerDropdown = isBottomNavigator;
  const clickedRef = useRef(false);

  const disabledDate = (current: Dayjs) => {
    if (minValue && maxValue) {
      return current < minValue || current > maxValue;
    } else if (minValue) return current < minValue;
    else if (maxValue) return current > maxValue;
    return false;
  };

  const onDisabledCellClick = (current: Dayjs) => {
    if (minValue && current < minValue) {
      openMessage(
        t("date_picker.min_date", {
          date: formatDate(minValue.unix(), false, true),
        }),
        "warning"
      );
    } else if (maxValue && current > maxValue) {
      openMessage(t("date_picker.no_future_dates"), "warning");
    }
  };

  const debouncedDisabledDateMessage = useDebouncedCallback({
    callback: onDisabledCellClick,
    wait: 1000,
    leading: true,
  });

  const rangePresets = getRangePresets(minValue);
  const mobilePresets = !hidePresets && isMobile && (
    <Row gutter={8} style={{ maxWidth: isDrawerDropdown ? "100%" : 268 }}>
      {rangePresets?.map((range, index) => {
        return (
          <Col key={index} span={8} style={{ lineHeight: "32px" }}>
            <Link
              style={{ fontSize: typography.fontSizes.S }}
              onClick={() => {
                onPresetsClick?.(range.value as [Dayjs | null, Dayjs | null]);
                setIsOpen(false);
              }}
            >
              {range.label}
            </Link>
          </Col>
        );
      })}
    </Row>
  );

  const rangePickerProps = {
    open: isOpen,
    onOpenChange: (open: boolean) => {
      setIsOpen(open);
      clickedRef.current = open;
    },
    inputReadOnly: isDrawerDropdown || isMobile,
    onMouseDown: () => {
      clickedRef.current = true;
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      if (!isOpen) {
        e.currentTarget.blur();
      }
    },
  };

  return (
    <Wrapper
      id={isDrawerDropdown ? undefined : containerUUID}
      $isMobile={isMobile}
      style={wrapperStyle}
    >
      <AntDatePicker.RangePicker
        classNames={{
          popup: { root: appTheme.isDarkMode ? "dark-mode" : "" },
        }}
        getPopupContainer={
          localRender || isDrawerDropdown
            ? () => document.getElementById(containerUUID)!
            : undefined
        }
        presets={hidePresets || isMobile ? undefined : rangePresets}
        renderExtraFooter={() => mobilePresets}
        disabledDate={disabledDate}
        placement="bottomRight"
        cellRender={(current, info) => {
          return (
            <div onClick={() => debouncedDisabledDateMessage(current as Dayjs)}>
              {info.originNode}
            </div>
          );
        }}
        placeholder={[t("labels.start_date"), t("labels.end_date")]}
        {...props}
        {...rangePickerProps}
      />
      {isDrawerDropdown && (
        <StyledDrawer
          placement="bottom"
          open={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
          closable={false}
          forceRender={true}
          zIndex={10000}
          size="auto"
        >
          <Wrapper id={containerUUID} $isMobile={isMobile} $isDrawer={true} />
          {!!bottomExtraElement && (
            <div style={{ padding: 16 }}>{bottomExtraElement}</div>
          )}
        </StyledDrawer>
      )}
    </Wrapper>
  );
};

export default RangePicker;

const Wrapper = styled.div<{
  $isMobile: boolean;
  $isDrawer?: boolean;
}>`
  ${(p) =>
    p.$isMobile &&
    `
    .ant-picker-panel:nth-of-type(2) {
      .ant-picker-body,
      .ant-picker-header {
        display: none;
      }
      .ant-picker-date-panel {
        position: absolute;
      }
      }
      .ant-picker-panel:not(:nth-of-type(2)) {
        .ant-picker-header >button {
           visibility: visible !important;
         }
      }
  `};
  ${(p) =>
    p.$isDrawer &&
    `
      .ant-picker-dropdown {
        position: relative;
        inset: 0 !important;
        padding: 0 !important;
      }
      .ant-picker-panel-container {
        box-shadow: none !important;
        width: 100%;
      }
      .ant-picker-panel-layout > div, .ant-picker-panels, .ant-picker-panel:not(:first-of-type), 
      .ant-picker-date-panel, .ant-picker-content {
        width: 100% !important;
      }
    `};
`;

const StyledDrawer = styled(Drawer)<{ $keyboardHeight?: number }>`
  .ant-drawer-content-wrapper {
    max-height: 500px;
    height: auto !important;
  }

  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  ${(p) =>
    p.$keyboardHeight &&
    `
        padding-bottom: ${p.$keyboardHeight}px;
      `};

  .ant-drawer-body {
    padding: 0 !important;
    overflow: hidden !important;
  }

  .ant-drawer-close {
    display: none;
  }
`;

export const rangePickerStyles = css`
  .ant-picker {
    width: 100%;
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.primaryText};
    border-color: ${(p) => p.theme.colors.mediumBorderGrey};
  }

  .ant-picker-clear {
    background: ${(p) => p.theme.colors.cardBgColor};
  }

  .ant-picker-range-arrow::after {
    border: 5px solid ${(p) => p.theme.colors.cardBgColor};
    border-color: ${(p) => p.theme.colors.cardBgColor}
      ${(p) => p.theme.colors.cardBgColor} transparent transparent;
  }

  .ant-picker-separator,
  .ant-picker-clear,
  .ant-picker-suffix,
  .ant-picker-input > input {
    color: ${(p) => p.theme.colors.primaryText};
  }

  .ant-picker-input > input::placeholder {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  .ant-picker-cell-in-view.ant-picker-cell-in-range::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-start:not(
      .ant-picker-cell-range-start-single
    )::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-end:not(
      .ant-picker-cell-range-end-single
    )::before {
    background-color: ${(p) => p.theme.colors.titleCardBgColor} !important;
  }

  .ant-picker-dropdown
    .ant-picker-cell-in-view.ant-picker-cell-selected
    .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner,
  .ant-picker-dropdown
    .ant-picker-cell-in-view.ant-picker-cell-range-start
    .ant-picker-cell-inner {
    background-color: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-picker-date-panel
    .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-start
    .ant-picker-cell-inner::after,
  .ant-picker-date-panel
    .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-end
    .ant-picker-cell-inner::after {
    background-color: ${(p) => p.theme.colors.titleCardBgColor};
  }

  .ant-picker-ranges .ant-picker-preset > .ant-tag {
    background: transparent;
    color: ${(p) => p.theme.colors.primaryBlue};
    border: none;
  }

  .ant-picker-range .ant-picker-active-bar {
    background: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-picker-presets {
    color: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-picker-dropdown
    .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover::before {
    background-color: ${(p) => p.theme.colors.tableHoverBgColor};
  }

  .ant-picker-dropdown .ant-picker-range-arrow::after {
    background: ${(p) => p.theme.colors.cardBgColor};
  }

  .ant-picker-dropdown .ant-picker-cell-disabled::before {
    background: ${(p) =>
      hexToRGB(p.theme.colors.lightestGrey, p.theme.isDarkMode ? 0.4 : 0.15)};
  }

  ${(p) => p.theme.isNewLook && newLookPickerStyles}
`;

// New look: pickers follow the field states (soft border, darker on hover,
// blue on focus) with muted icons and legible placeholders.
const newLookPickerStyles = css`
  .ant-picker:not(.ant-picker-borderless):not(.ant-picker-status-error):not(
      .ant-picker-focused
    ) {
    border-color: ${(p) => p.theme.colors.lightBorderGrey};
    &:hover {
      border-color: ${(p) => p.theme.colors.mediumBorderGrey};
    }
  }
  .ant-picker-separator,
  .ant-picker-suffix {
    color: ${(p) => p.theme.colors.secondaryText};
  }
  .ant-picker-input > input::placeholder {
    color: ${(p) => getNewLookPlaceholderColor(p.theme)};
  }
  .ant-picker-presets ul li {
    border-radius: 8px;
  }
`;
